import type { TableId } from '@casino/core';
import type { TableRules as CrapsRules } from '@casino/craps-engine';

export type TableShape = 'craps' | 'roulette' | 'poker';

function fmtDollars(cents: number): string {
  return `$${Math.floor(cents / 100).toLocaleString('en-US')}`;
}

/** Build the stake-line copy for the craps:standard tile from its rules so
 *  the floor never drifts from what the table actually accepts. */
export function formatCrapsStake(rules: Pick<CrapsRules, 'tableMin' | 'tableMax' | 'oddsCap'>): string {
  // oddsCap reads as `3-4-5x` in code; the floor renders the typographic ×.
  const odds = rules.oddsCap.replace('x', '×');
  return `${fmtDollars(rules.tableMin)}–${fmtDollars(rules.tableMax)} · ${odds} odds`;
}

export interface LobbyTableTile {
  id: TableId;
  label: string;
  stakeText: string;
  shape: TableShape;
  /** Anchor x, y in canvas-buffer px, relative to the lobby canvas. */
  x: number;
  y: number;
  /** Display size of the table silhouette. */
  w: number;
  h: number;
  unlocked: boolean;
  /** Used for placeholder tiles that aren't real games. */
  ghost?: boolean;
  /** Copy shown when the player taps a locked tile (e.g. "Reach $5,000 to unlock"). */
  lockReason?: string;
}

export interface LobbyRoom {
  id: 'entrance' | 'pit' | 'salon-prive';
  label: string;
  /** y-range on the canvas occupied by this room. */
  yStart: number;
  yEnd: number;
  /** Tiles within this room. */
  tiles: LobbyTableTile[];
  /** Rooms with a divider get a rangebar pill drawn at `dividerY`. */
  velvetRope: boolean;
  /** Centerline y of the room's rangebar pill divider, in canvas-buffer
   *  px. For most rooms this is yStart + a small inset; for the entrance
   *  it sits BELOW the tutorial tile so the tutorial reads as a preamble
   *  card above the divider. */
  dividerY: number;
}

export interface LobbyLayout {
  /** Total scrollable height. */
  scrollHeight: number;
  rooms: LobbyRoom[];
}

/**
 * Portrait lobby layout. Ships the three craps tiers (standard unlocked;
 * high-limit / whale gated behind bankroll), plus the tutorial preamble.
 *
 * `W` is canvas-buffer pixels. `dpr` scales touch targets and y-spacing into
 * CSS pixels so tiles read correctly on retina phones.
 *
 * `opts.crapsRules` sources the stake-line copy from the actual table rules so
 * the floor reads what the table accepts. Without it, falls back to the
 * historical hardcoded strings.
 */
export interface LobbyLayoutOptions {
  crapsRules?: Pick<CrapsRules, 'tableMin' | 'tableMax' | 'oddsCap'>;
}

export function lobbyLayoutPortrait(
  W: number,
  dpr = 1,
  opts: LobbyLayoutOptions = {},
): LobbyLayout {
  const cx = W / 2;
  // Cap the tile-sizing width to a portrait-phone column (~480 CSS px) so the
  // lobby letterboxes on landscape desktop instead of stretching the card
  // edge-to-edge. Rooms still fill the full canvas; tiles sit centered in a
  // phone-width band.
  const designW = Math.min(W, Math.round(480 * dpr));
  const px = (n: number) => Math.round(n * dpr);

  // Unified tile width — all tiles sit in the same column inside the card
  // (no more wider/narrower variants for ghost or high-limit). Matches the
  // mashup, where every .tile element spans the full card content width.
  const tileW = designW * 0.85;
  const tileX = cx - tileW / 2;

  // y-axis is built from named constants so the spacing is legible at a
  // glance and a single tweak doesn't require chasing down sibling y-coords.
  // Everything below the brand block (headerH) cascades from these.
  const headerH = 145;          // brand block + bottom margin
  const tutH = 60;              // tutorial-card height (label + stake only)
  const stdH = 95;              // standard tile height (handles either
                                //   locked-with-lock-reason or unlocked-with-Enter)
  const dividerTop = 35;        // vertical breathing space above a divider pill
  const dividerBot = 35;        // and below it

  // Entrance — tutorial preamble + ENTRANCE divider + craps standard.
  const tutorialY     = headerH;
  const room1DivY     = tutorialY + tutH + dividerTop;
  const room1FirstY   = room1DivY + dividerBot;
  const room1EndY     = room1FirstY + stdH;

  // Pit — locked HL craps.
  const room2DivY     = room1EndY + dividerTop;
  const room2FirstY   = room2DivY + dividerBot;
  const room2EndY     = room2FirstY + stdH;

  // Salon Privé — locked whale craps.
  const room3DivY     = room2EndY + dividerTop;
  const room3FirstY   = room3DivY + dividerBot;
  const room3EndY     = room3FirstY + stdH;

  const room1: LobbyRoom = {
    id: 'entrance',
    label: 'ENTRANCE',
    yStart: px(headerH), yEnd: px(room1EndY),
    velvetRope: true,
    dividerY: px(room1DivY),
    tiles: [
      {
        id: 'craps:tutorial',
        label: 'Craps Tutorial',
        stakeText: 'Learn · Free',
        shape: 'craps',
        x: tileX, y: px(tutorialY),
        w: tileW, h: px(tutH),
        unlocked: true,
      },
      {
        id: 'craps:standard',
        label: 'Craps',
        stakeText: opts.crapsRules ? formatCrapsStake(opts.crapsRules) : '$5–$1,000 · 3-4-5× odds',
        shape: 'craps',
        x: tileX, y: px(room1FirstY),
        w: tileW, h: px(stdH),
        unlocked: true,
      },
    ],
  };

  const room2: LobbyRoom = {
    id: 'pit',
    label: 'THE PIT',
    yStart: px(room1EndY), yEnd: px(room2EndY),
    velvetRope: true,
    dividerY: px(room2DivY),
    tiles: [
      {
        id: 'craps:high-limit',
        label: 'Craps',
        stakeText: '$100–$10,000',
        shape: 'craps',
        x: tileX, y: px(room2FirstY),
        w: tileW, h: px(stdH),
        unlocked: false,
        lockReason: 'Reach $5,000 bankroll to unlock',
      },
    ],
  };

  const room3: LobbyRoom = {
    id: 'salon-prive',
    label: 'SALON PRIVÉ',
    yStart: px(room2EndY), yEnd: px(room3EndY),
    velvetRope: true,
    dividerY: px(room3DivY),
    tiles: [
      {
        id: 'craps:whale',
        label: 'Craps',
        stakeText: '$1,000–$100,000',
        shape: 'craps',
        x: tileX, y: px(room3FirstY),
        w: tileW, h: px(stdH),
        unlocked: false,
        lockReason: 'Reach $50,000 bankroll to unlock',
      },
    ],
  };

  return { scrollHeight: px(room3EndY + 28), rooms: [room1, room2, room3] };
}
